import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import "./TherapistDashboard.css";

/* ─────────────────────────────────────────────────────────────────
   CdtActionsCell
   Use a column's  `actions` array instead of `cell` to get
   automatic overflow-menu behaviour:

   actions: [
     { icon: "✏️", label: "Edit",   desc: "Edit this row",    onClick: (row) => ... },
     { icon: "🗑️", label: "Delete", desc: "Remove this item", onClick: (row) => ... },
     ...
   ]

   ≤ 3 actions  → all shown as icon buttons
   > 3 actions  → first 2 shown + ⠿ kebab; rest in portal dropdown
───────────────────────────────────────────────────────────────── */
function CdtActionsCell({ actions, row, rowIndex }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const MAX_VISIBLE = 2;
  const shown    = actions.length <= 3 ? actions : actions.slice(0, MAX_VISIBLE);
  const overflow = actions.length >  3 ? actions.slice(MAX_VISIBLE) : [];

  const handleKebabClick = (e) => {
    e.stopPropagation();
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  const iconBtnStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px 6px",
    border: "none",
    borderRadius: "6px",
    background: "transparent",
    cursor: "pointer",
    fontSize: 22,
    flexShrink: 0,
    transition: "background .14s",
  };

  return (
    <div className="cdt-action-cell">
      {shown.map((action, i) => (
        <button
          key={i}
          title={action.label}
          style={iconBtnStyle}
          onClick={(e) => { e.stopPropagation(); action.onClick(row, rowIndex); }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(33,150,243,.10)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
        >
          {action.icon}
        </button>
      ))}

      {overflow.length > 0 && (
        <>
          <button
            ref={btnRef}
            className="cdt-kebab-btn"
            title="More actions"
            onClick={handleKebabClick}
          >
            ⠿
          </button>

          {open && ReactDOM.createPortal(
            <div
              className="cdt-overflow-menu"
              style={{ position: "fixed", top: menuPos.top, right: menuPos.right, zIndex: 9999 }}
              onClick={e => e.stopPropagation()}
            >
              {overflow.map((action, i) => (
                <React.Fragment key={i}>
                  {i > 0 && i < overflow.length && action.sep && (
                    <div className="cdt-overflow-sep" />
                  )}
                  <button
                    className="cdt-overflow-item"
                    onClick={() => { setOpen(false); action.onClick(row, rowIndex); }}
                  >
                    <span className="cdt-oi-icon">{action.icon}</span>
                    <span>
                      <span className="cdt-oi-label">{action.label}</span>
                      {action.desc && <span className="cdt-oi-desc">{action.desc}</span>}
                    </span>
                  </button>
                </React.Fragment>
              ))}
            </div>,
            document.body
          )}
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   CustomDataTable  (class component — all original behaviour kept)
───────────────────────────────────────────────────────────────── */
class CustomDataTable extends React.Component {
  constructor(props) {
    super(props);

    const data = props.data || [];

    this.state = {
      sortColumn: null,
      sortAsc: true,
      currentPage: 1,
      selectedRows: [],
      deselectedRowIds: [],
      data: data,
      paginationPerPage: props.paginationPerPage || 300,
      filters: {},
      expandedRowId: null,
    };

    this.handleSort = this.handleSort.bind(this);
    this.handleSelectRow = this.handleSelectRow.bind(this);
    this.handleSelectAll = this.handleSelectAll.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);
    this.handleRowsPerPageChange = this.handleRowsPerPageChange.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleToggleExpand = this.handleToggleExpand.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data) {
      this.setState({ data: this.props.data });
    }
    if (prevProps.selectedRows !== this.props.selectedRows) {
      this.setState({ selectedRows: this.props.selectedRows || [] });
    }
  }

  getValue(row, selector) {
    if (typeof selector === "function") return selector(row);
    if (typeof selector === "string") return row[selector];
    return null;
  }

  handleSort(column) {
    if (!column.selector) return;

    var selector = column.selector;
    var isSameColumn = this.state.sortColumn && this.state.sortColumn.selector === selector;
    var newSortAsc = isSameColumn ? !this.state.sortAsc : true;
    var sortedData = this.state.data.slice().sort(function(a, b) {
      var aVal = this.getValue(a, selector);
      var bVal = this.getValue(b, selector);

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();

      if (aVal === bVal) return 0;
      return newSortAsc ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    }.bind(this));

    this.setState({
      data: sortedData,
      sortColumn: column,
      sortAsc: newSortAsc,
    });
  }

  handleSelectRow(row, checked) {
    var selectedRows = this.state.selectedRows.slice();
    var deselectedRowIds = this.state.deselectedRowIds.slice();

    const isAlreadySelected = selectedRows.some(r => r._id === row._id);

    if (checked) {
      deselectedRowIds = deselectedRowIds.filter(function(id) {
        return id !== row._id;
      });
      if (!isAlreadySelected) {
       selectedRows.push(row);
      }
    } else {
      console.log("disableDeselect isAlreadySelected=", this.props.disableDeselect, isAlreadySelected);

      if (this.props.disableDeselect === true && isAlreadySelected) {
        console.log("Blocked deselect =");
        return;
      }

      if (deselectedRowIds.indexOf(row._id) === -1) {
        deselectedRowIds.push(row._id);
      }
      selectedRows = selectedRows.filter(function(r) {
        return r._id !== row._id;
      });
    }

    this.setState({
      selectedRows: selectedRows,
      deselectedRowIds: deselectedRowIds,
    });

    var combinedSelected = selectedRows.concat(
      (this.props.data || []).filter(function(row) {
        return (
          this.props.selectableRowSelected &&
          this.props.selectableRowSelected(row) &&
          deselectedRowIds.indexOf(row._id) === -1 &&
          !selectedRows.some(function(r) { return r._id === row._id; })
        );
      }.bind(this))
    );

    if (this.props.onSelectedRowsChange) {
      this.props.onSelectedRowsChange({ selectedRows: combinedSelected });
    }
  }

  handleSelectAll(checked) {
    if (this.props.removeSelectAll) return;
    var data = this.state.data;

    if (checked) {
      var newSelected = data.slice();
      this.setState({ selectedRows: newSelected, deselectedRowIds: [] });
      if (this.props.onSelectedRowsChange) {
        this.props.onSelectedRowsChange({ selectedRows: newSelected });
      }
    } else {
      var newDeselectedIds = data.map(function(row) { return row._id; });
      this.setState({ deselectedRowIds: newDeselectedIds, selectedRows: [] });
      if (this.props.onSelectedRowsChange) {
        this.props.onSelectedRowsChange({ selectedRows: [] });
      }
    }
  }

  handlePageChange(newPage) {
    var data = this.state.data;
    var paginationPerPage = this.state.paginationPerPage;
    var totalPages = Math.max(1, Math.ceil((this.props.serverTotal || data.length) / paginationPerPage));
    if (newPage < 1 || newPage > totalPages) return;
    this.setState({ currentPage: newPage });
    if (this.props.onPageChange) this.props.onPageChange(newPage, paginationPerPage);
  }

  isRowSelected(row) {
    if (this.state.deselectedRowIds.indexOf(row._id) !== -1) return false;
    if (this.state.selectedRows.some(function(r) { return r._id === row._id; })) return true;
    if (this.props.selectableRowSelected) return this.props.selectableRowSelected(row);
    return false;
  }

  getRowBg(row, index) {
    if (row?.status === 'OnGoing') return null;
    return index % 2 === 0 ? '#fff' : '#fafbfd';
  }

  handleRowsPerPageChange(e) {
    var newLimit = parseInt(e.target.value, 10);
    this.setState({ paginationPerPage: newLimit, currentPage: 1 });
    if (this.props.onRowsPerPageChange) this.props.onRowsPerPageChange(newLimit);
  }

  handleFilterChange(selector, value) {
    var filters = Object.assign({}, this.state.filters);
    filters[selector] = value;
    this.setState({ filters: filters, currentPage: 1 });
  }

  handleToggleExpand(rowId) {
    this.setState(function(prevState) {
      return { expandedRowId: prevState.expandedRowId === rowId ? null : rowId };
    });
  }

  render() {
    var columns = this.props.columns || [];
    var selectableRows = this.props.selectableRows;
    var ExpandableComp = this.props.expandableRowsComponent || null;
    var expandedRowId = this.state.expandedRowId;

    var data = this.state.data;
    var currentPage = this.state.currentPage;
    var sortColumn = this.state.sortColumn;
    var sortAsc = this.state.sortAsc;
    var paginationPerPage = this.state.paginationPerPage;
    var filters = this.state.filters;

    var filteredData = data.filter(function(row) {
      return columns.every(function(col) {
        if (col.filterable === false) return true;
        var filterValue = filters[col.selector];
        if (!filterValue) return true;
        var cellValue = this.getValue(row, col.selector);
        if (cellValue == null) return false;
        return String(cellValue).toLowerCase().indexOf(filterValue.toLowerCase()) !== -1;
      }.bind(this));
    }.bind(this));

    var sortedData = filteredData.slice();

    if (sortColumn && sortColumn.selector) {
      var selector = sortColumn.selector;
      sortedData.sort(function(a, b) {
        var aVal = this.getValue(a, selector);
        var bVal = this.getValue(b, selector);
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (typeof aVal === "string") aVal = aVal.toLowerCase();
        if (typeof bVal === "string") bVal = bVal.toLowerCase();
        if (aVal === bVal) return 0;
        return sortAsc ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
      }.bind(this));
    }

    var sortedWithSelection = sortedData.slice();
    sortedWithSelection.sort(function(a, b) {
      var aSelected = this.isRowSelected(a) ? 0 : 1;
      var bSelected = this.isRowSelected(b) ? 0 : 1;
      return aSelected - bSelected;
    }.bind(this));

    var displayTotal = this.props.serverTotal || sortedWithSelection.length;
    var totalPages = Math.max(1, Math.ceil(displayTotal / paginationPerPage));
    var startIdx = (currentPage - 1) * paginationPerPage;
    var endIdx = Math.min(startIdx + paginationPerPage, sortedWithSelection.length);
    var pageData = sortedWithSelection.slice(startIdx, endIdx);

    var allSelectedOnPage = pageData.length > 0 && pageData.every(this.isRowSelected.bind(this));

    return (
      <div>
        {/* ── Table ── */}
        <div style={{
          overflowX: "auto",
          maxHeight: "600px",
          borderRadius: "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,.04), 0 4px 16px rgba(0,0,0,.06)",
          border: "1px solid #e2e8f0",
          overflowY: "auto",
          background: "#fff",
        }}>
          <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%", fontFamily: "'Inter','SF Pro Display',system-ui,sans-serif", fontSize: "13px" }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
              {/* Column headers */}
              <tr>
                {ExpandableComp && (
                  <th className="cdt-th" style={{ width: 32, textAlign: "center" }}></th>
                )}
                {selectableRows && (
                  <th className="cdt-th" style={{ width: 36, textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={allSelectedOnPage}
                      onChange={(e) => this.handleSelectAll(e.target.checked)}
                      aria-label="Select all rows"
                    />
                  </th>
                )}
                {columns.map((col, idx) => (
                  !col.omit && (
                    <th
                      key={idx}
                      className="cdt-th"
                      onClick={() => this.handleSort(col)}
                      scope="col"
                      aria-sort={
                        sortColumn && sortColumn.selector === col.selector
                          ? (sortAsc ? "ascending" : "descending")
                          : "none"
                      }
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ flexGrow: 1 }}>{col.name}</span>
                        {sortColumn && sortColumn.selector === col.selector && (
                          <span aria-hidden="true" style={{ opacity: .7 }}>
                            {sortAsc ? "▲" : "▼"}
                          </span>
                        )}
                      </div>
                    </th>
                  )
                ))}
              </tr>

              {/* Filter row */}
              <tr>
                {ExpandableComp && <th className="cdt-filter-th"></th>}
                {selectableRows && <th className="cdt-filter-th"></th>}
                {columns.map((col, idx) => (
                  !col.omit && (
                    <th key={"filter" + idx} className="cdt-filter-th">
                      {col.filterable === false ? null : (
                        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                          <input
                            type="text"
                            placeholder="Filter…"
                            value={filters[col.selector] || ""}
                            onChange={(e) => this.handleFilterChange(col.selector, e.target.value)}
                            className="cdt-filter-input"
                            aria-label={"Filter for " + col.name}
                          />
                          {filters[col.selector] && (
                            <span
                              onClick={() => this.handleFilterChange(col.selector, "")}
                              style={{
                                position: "absolute", right: 7, cursor: "pointer",
                                color: "#999", fontWeight: "bold", fontSize: 18, lineHeight: 1, userSelect: "none",
                              }}
                              aria-label={`Clear filter for ${col.name}`}
                              role="button"
                            >
                              ×
                            </span>
                          )}
                        </div>
                      )}
                    </th>
                  )
                ))}
              </tr>
            </thead>

            <tbody>
              {pageData.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.filter(c => !c.omit).length + (selectableRows ? 1 : 0) + (ExpandableComp ? 1 : 0)}
                    style={{ textAlign: "center", padding: "48px 32px", color: "#94a3b8", fontSize: 14, fontFamily: "'Inter','SF Pro Display',system-ui,sans-serif" }}
                  >
                    No data available
                  </td>
                </tr>
              ) : (
                pageData.map((row, rowIndex) => {
                  var isSelected = this.isRowSelected(row);
                  var isOngoing  = row?.status === 'OnGoing';
                  var rowClass   = "cdt-row" + (isSelected ? " cdt-selected" : "") + (isOngoing ? " cdt-ongoing" : "");
                  var rowBg      = this.getRowBg(row, rowIndex);
                  var rowId      = row._id || rowIndex;
                  var isExpanded = ExpandableComp && expandedRowId === rowId;
                  var colCount   = columns.filter(function(c) { return !c.omit; }).length;

                  var conditionalStyle = {};
                  if (this.props.conditionalRowStyles) {
                    for (var k = 0; k < this.props.conditionalRowStyles.length; k++) {
                      var cs = this.props.conditionalRowStyles[k];
                      if (cs.when && cs.when(row)) {
                        Object.assign(conditionalStyle, cs.style);
                        break;
                      }
                    }
                  }

                  return (
                    <React.Fragment key={row._id || rowIndex}>
                      <tr
                        className={rowClass}
                        style={{ backgroundColor: isSelected || isOngoing ? undefined : (conditionalStyle.backgroundColor || rowBg), ...conditionalStyle, cursor: (selectableRows || this.props.onRowClick || ExpandableComp) ? "pointer" : "default", userSelect: "none" }}
                        onClick={() => {
                          if (ExpandableComp) { this.handleToggleExpand(rowId); return; }
                          if (selectableRows && !isSelected) this.handleSelectRow(row, true);
                          if (this.props.onRowClick) this.props.onRowClick(row, rowIndex);
                        }}
                        aria-selected={isSelected}
                        tabIndex={-1}
                      >
                        {ExpandableComp && (
                          <td className="cdt-td" style={{ textAlign: "center", width: 32, padding: "4px 6px" }}>
                            <button
                              style={{
                                background: isExpanded ? "#eff6ff" : "none",
                                border: isExpanded ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
                                borderRadius: 6, cursor: "pointer", fontSize: 12,
                                padding: "3px 6px", color: isExpanded ? "#1d4ed8" : "#94a3b8",
                                lineHeight: 1, transition: "all 0.15s",
                                fontWeight: 700,
                              }}
                              onClick={(e) => { e.stopPropagation(); this.handleToggleExpand(rowId); }}
                              aria-label={isExpanded ? "Collapse row" : "Expand row"}
                            >
                              {isExpanded ? "▾" : "▸"}
                            </button>
                          </td>
                        )}
                        {selectableRows && (
                          <td className="cdt-td" style={{ textAlign: "center", width: 36 }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (this.props.disableDeselect === true && isSelected) {
                                  console.log("blocking deselect 11");
                                  e.preventDefault();
                                  return;
                                }
                                this.handleSelectRow(row, e.target.checked);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={"Select row " + (rowIndex + 1)}
                            />
                          </td>
                        )}
                        {columns.map((col, colIndex) => (
                          !col.omit && (
                            <td key={colIndex} className="cdt-td">
                              {col.actions
                                ? <CdtActionsCell actions={col.actions(row, rowIndex)} row={row} rowIndex={rowIndex} />
                                : col.cell
                                  ? col.cell(row, rowIndex)
                                  : this.getValue(row, col.selector)
                              }
                            </td>
                          )
                        ))}
                      </tr>
                      {ExpandableComp && isExpanded && (
                        <tr>
                          <td
                            colSpan={colCount + (selectableRows ? 1 : 0) + 1}
                            style={{ padding: 0, borderBottom: "2px solid #dbeafe" }}
                          >
                            <ExpandableComp data={row} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        <div style={{
          marginTop: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "12px",
          color: "#64748b",
          flexWrap: "nowrap",
          gap: 12,
          padding: "0 4px",
          fontFamily: "'Inter','SF Pro Display',system-ui,sans-serif",
        }}>
          <div>
            <span style={{ color: "#94a3b8" }}>Showing </span>
            <strong style={{ color: "#0f172a" }}>{sortedWithSelection.length === 0 ? 0 : startIdx + 1}–{endIdx}</strong>
            <span style={{ color: "#94a3b8" }}> of </span>
            <strong style={{ color: "#0f172a" }}>{displayTotal}</strong>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label htmlFor="rowsPerPage" style={{ color: "#94a3b8", fontSize: 11, fontWeight: 600 }}>Rows</label>
            <select
              id="rowsPerPage"
              value={paginationPerPage}
              onChange={this.handleRowsPerPageChange}
              style={{ padding: "5px 10px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 12, cursor: "pointer", color: "#334155", background: "#fff" }}
            >
              {[100, 200, 300, 400].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button className="cdt-page-btn" onClick={() => this.handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              Prev
            </button>
            <span style={{ color: "#334155", fontWeight: 700, fontSize: 12 }}>
              {currentPage} / {totalPages}
            </span>
            <button className="cdt-page-btn" onClick={() => this.handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
              Next
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export { CdtActionsCell };
export default CustomDataTable;
